import useGetTerminologyFilterTypes from 'Modules/Communication/shared/hooks/useGetTerminologyFilterTypes'
import { FilterTypeEnum } from 'Modules/Communication/shared/models/Recipients'
import { FC, useMemo, useState } from 'react'
import { Checkbox } from '@bbl-digital/snorre'
import { useDispatch, useSelector } from 'react-redux'
import AddressPicker from 'shared/components/AddressPicker'
import DropdownFilter from 'shared/components/DropdownFilter'
import { setRecipientsFilters } from 'shared/store/sendMessage/recipients'
import {
  getMessageRecipients,
  getRecipientMeters,
  getRecipientObjects,
  getRecipientsBuildingTypes,
  getRecipientsCommittees,
} from 'shared/store/sendMessage/recipients/selectors'
import {
  DropdownWrapper,
  Wrapper,
  WrapperContent,
  CheckboxWrapper,
} from './styles'
import useInitRecipientsFilters from 'Modules/Communication/shared/hooks/useInitRecipientsFilters'
import { useFetchAllBuildingsQuery } from 'shared/microServices/housing/housing.service'

const RecipientsListFilters: FC = () => {
  const { data: buildings, isLoading, error } = useFetchAllBuildingsQuery()

  const [checked, setChecked] = useState<boolean>(false)
  const dispatch = useDispatch()
  const filterTypes = useGetTerminologyFilterTypes()

  const { data: buildingTypes } = useSelector(getRecipientsBuildingTypes)
  const { data: committees } = useSelector(getRecipientsCommittees)
  const meterTypes = useSelector(getRecipientMeters)
  const objectTypes = useSelector(getRecipientObjects)
  const { filters } = useSelector(getMessageRecipients)

  useInitRecipientsFilters()

  const isOnMeters = filters.filterType === FilterTypeEnum.Meters
  const isOnObjects = filters.filterType === FilterTypeEnum.Objects
  const isOnBoardMembers = filters.filterType === FilterTypeEnum.BoardMembers
  const isOnHabitants =
    filters.filterType === FilterTypeEnum.Owners ||
    filters.filterType === FilterTypeEnum.Tenants ||
    filters.filterType === FilterTypeEnum.Cohabitant

  const handleFilters =
    (action: string) => (value?: number | string | null) => {
      const val = value || null
      dispatch(setRecipientsFilters({ [action]: val }))
    }

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      handleFilters('addressId')(null)
    } else {
      handleFilters('buildingTypeId')(null)
    }

    setChecked(e.target.checked)
  }

  const addresses = useMemo(() => {
    if (filters.buildingId)
      return buildings?.find((building) => building.id === filters.buildingId)
        ?.addresses
    return buildings?.map((building) => building.addresses).flat()
  }, [buildings, filters.buildingId])

  return (
    <Wrapper>
      <CheckboxWrapper>
        {isOnHabitants && Number(buildingTypes?.length) > 2 && (
          <Checkbox onChange={handleCheckbox} checked={checked}>
            Gruppér på bygningstype
          </Checkbox>
        )}
      </CheckboxWrapper>

      <WrapperContent>
        {isOnHabitants && (
          <>
            {checked && Number(buildingTypes?.length) > 2 && (
              <DropdownWrapper>
                <DropdownFilter
                  onChange={handleFilters('buildingTypeId')}
                  value={filters.buildingTypeId}
                  advancedValues={buildingTypes?.map(({ type, id }) => ({
                    id,
                    displayText: type,
                  }))}
                />
              </DropdownWrapper>
            )}

            {!checked && (
              <div className="flex flex-wrap flex-col sm:!flex-row  gap-1 justify-end">
                {buildings?.length > 1 && (
                  <DropdownWrapper>
                    <DropdownFilter
                      onChange={(e) => {
                        handleFilters('buildingId')(e)
                        dispatch(
                          setRecipientsFilters({
                            addressId: null,
                          })
                        )
                      }}
                      value={filters.buildingId}
                      advancedValues={[
                        {
                          id: null,
                          displayText: 'Alle bygninger',
                        },
                        // eslint-disable-next-line no-unsafe-optional-chaining
                        ...buildings?.map(({ id, name }) => ({
                          id,
                          displayText: name,
                        })),
                      ]}
                    />
                  </DropdownWrapper>
                )}
                <AddressPicker
                  onChangeAddress={handleFilters('addressId')}
                  selected={filters.addressId}
                  data={addresses}
                  loading={isLoading}
                  error={error as string}
                />
              </div>
            )}
          </>
        )}

        {isOnBoardMembers && Number(committees?.length) > 1 && (
          <DropdownWrapper>
            <DropdownFilter
              onChange={handleFilters('boardId')}
              value={filters.boardId}
              advancedValues={committees?.map(({ name, id }) => ({
                id,
                displayText: name,
              }))}
            />
          </DropdownWrapper>
        )}

        {isOnMeters && Boolean(meterTypes.length) && (
          <DropdownWrapper>
            <DropdownFilter
              onChange={handleFilters('meterTypeNo')}
              value={filters.meterTypeNo}
              advancedValues={meterTypes.map((meter) => ({
                id: meter.meterTypeId,
                displayText: meter.meterType,
              }))}
            />
          </DropdownWrapper>
        )}

        {isOnObjects && Boolean(objectTypes.length) && (
          <DropdownWrapper>
            <DropdownFilter
              onChange={handleFilters('objectTypeNo')}
              value={filters.objectTypeNo}
              advancedValues={objectTypes.map((object) => ({
                id: object.objectTypeId,
                displayText: object.objectType,
              }))}
            />
          </DropdownWrapper>
        )}

        <DropdownWrapper>
          <DropdownFilter
            advancedValues={filterTypes}
            onChange={handleFilters('filterType')}
            value={filters.filterType}
          />
        </DropdownWrapper>
      </WrapperContent>
    </Wrapper>
  )
}

export default RecipientsListFilters
